import { FC, MouseEvent, useCallback, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { FormProps } from '../helpers';
import ListToggle from '../list-toggle';
import { UiOption } from '@/lib/helpers';
import { Box, Stack, Theme } from '@mui/material';
import Icon from '@/components/icon';
import Text from '@/components/text';
import useDrawer from '@/hooks/use-drawer.hook';
import { useQuery } from 'react-query';
import { getAllGroups } from '@/lib/services/group.service';
import { QueryKey } from '@/lib/query-client';
import useQueryHelper from '@/hooks/use-query-helper';

interface Props extends FormProps {
  options: UiOption[];
  value: string[];
  onChange: (options: UiOption[]) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  groupOption: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    '& .label': {
      maxWidth: '90%',
    },
  },
  groupListToggle: {
    '& .MuiTypography-root': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'block',
      flexGrow: 1,
    },
  },
}));

const GroupsListToggle: FC<Props> = (props) => {
  const styles = useStyles();
  const { openDrawer } = useDrawer();

  const query = useQuery([QueryKey.GroupsList], async () => getAllGroups());
  const { data: groups } = query;
  const { showLoader } = useQueryHelper(query);

  const handleView = useCallback(
    (event: MouseEvent, groupId: string) => {
      event.stopPropagation(); // prevent clicking on info from opening the button accordion
      event.preventDefault();
      openDrawer('group', { id: groupId, hideUpdateMenu: true });
    },
    [openDrawer]
  );

  const { value } = props;

  const options: UiOption[] = useMemo(() => {
    return (groups || []).map(({ id, name }) => {
      return {
        label: (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            p={1}
            className={styles.groupOption}
          >
            <Box className="label">
              <Text dotdot>{name}</Text>
            </Box>
            <Icon
              name="info-circle"
              onClick={(event: MouseEvent) => handleView(event, id)}
              size="small"
              color="grey"
            />
          </Stack>
        ),
        value: id,
        meta: { keyword: name },
      };
    });
  }, [groups, handleView, styles.groupOption]);

  const listToggleProps = { ...props, value: value || [], options };

  return (
    <Box className={styles.groupListToggle}>
      <ListToggle
        {...listToggleProps}
        label="Groups"
        leftTitle="Available Groups"
        rightTitle="Assigned Groups"
        loadState={showLoader ? 'loading' : 'loaded'}
      />
    </Box>
  );
};

export default GroupsListToggle;
